import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AtlasProvider, useAtlas } from "@/context/AtlasContext";
import { getTenant, setTenant } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/components/shell/AppShell";
import Login from "@/pages/Login";
import AccessDenied from "@/pages/AccessDenied";
import Dashboard from "@/pages/Dashboard";
import AtlasAIPage from "@/pages/AtlasAIPage";
import Decisions from "@/pages/Decisions";
import Missions from "@/pages/Missions";
import Portfolio from "@/pages/Portfolio";
import Ingest from "@/pages/admin/Ingest";
import Companies from "@/pages/Companies";
import CompaniesReport from "@/pages/CompaniesReport";
import Reports from "@/pages/Reports";
import BrandedLogin from "@/pages/BrandedLogin";
import MarketingHome from "@/pages/marketing/MarketingHome";
import Budget from "@/pages/marketing/Budget";
import Channels from "@/pages/marketing/Channels";
import EmployeeZero from "@/pages/EmployeeZero";
import AgentDetail from "@/pages/AgentDetail";
import Marketplace from "@/pages/Marketplace";
import MarketplaceCategories from "@/pages/MarketplaceCategories";
import { AgentActivityPage, HiringRequestsPage } from "@/pages/EmployeeZeroExtras";
import { AcceptInvite, ForgotPassword, ResetPassword } from "@/pages/auth/AccountPages";
import ForcePasswordChange from "@/pages/auth/ForcePasswordChange";
import Organizations from "@/pages/crm/Organizations";
import OrganizationDetail from "@/pages/crm/OrganizationDetail";
import ContactProfile from "@/pages/crm/ContactProfile";
import People from "@/pages/crm/People";
import PeopleTeam from "@/pages/PeopleTeam";
import OrgChart from "@/pages/OrgChart";
import Opportunities from "@/pages/crm/Opportunities";
import Initiatives from "@/pages/tactics/Initiatives";
import Projects from "@/pages/tactics/Projects";
import ProjectDetail from "@/pages/tactics/ProjectDetail";
import Items from "@/pages/inventory/Items";
import ItemDetail from "@/pages/inventory/ItemDetail";
import Locations from "@/pages/inventory/Locations";
import Movements from "@/pages/inventory/Movements";
import Campaigns from "@/pages/marketing/Campaigns";
import CampaignDetail from "@/pages/marketing/CampaignDetail";
import Company from "@/pages/admin/Company";
import Industries from "@/pages/admin/Industries";
import Users from "@/pages/admin/Users";
import AdminPeople from "@/pages/admin/AdminPeople";
import Teams from "@/pages/admin/Teams";
import Roles from "@/pages/admin/Roles";
import Applications from "@/pages/admin/Applications";
import Integrations from "@/pages/admin/Integrations";
import Audit from "@/pages/admin/Audit";
import Knowledge from "@/pages/admin/Knowledge";
import AIActivity from "@/pages/admin/AIActivity";
import Operations from "@/pages/admin/Operations";
import Support from "@/pages/admin/Support";
import Automation from "@/pages/admin/Automation";
import TeamSheetDashboard from "@/pages/team_sheet/Dashboard";
import CoachesBoard from "@/pages/team_sheet/CoachesBoard";
import Drafts from "@/pages/team_sheet/Drafts";
import DraftReview from "@/pages/team_sheet/DraftReview";
import ScenarioCompare from "@/pages/team_sheet/ScenarioCompare";
import PulseOverview from "@/pages/pulse/PulseOverview";
import PulseExplorer from "@/pages/pulse/PulseExplorer";
import PulseSources from "@/pages/pulse/PulseSources";
import Recruiting from "@/pages/team_sheet/Recruiting";
import UploadCentre from "@/pages/team_sheet/UploadCentre";
import ClientProfile from "@/pages/team_sheet/ClientProfile";
import Clients from "@/pages/clients/Clients";
import ClientBoard from "@/pages/clients/ClientBoard";
import FilesApp from "@/pages/files/FilesApp";
import Documents from "@/pages/documents/Documents";
import MyWork from "@/pages/work/MyWork";
import AllWork from "@/pages/work/AllWork";
import Boards from "@/pages/work/Boards";
import Board from "@/pages/work/Board";
import ItemRedirect from "@/pages/work/ItemRedirect";
import Settings from "@/pages/Settings";
import { PortalShell, PortalHome, PortalTickets, PortalTasks, PortalTicket, PortalDocuments, PortalAsk } from "@/pages/portal/Portal";

const Splash = ({ text = "Loading Total Operations…" }) => <div className="h-screen flex items-center justify-center text-slate-400">{text}</div>;

function Protected() {
  const { user, ctx } = useAtlas();
  if (user === undefined) return <Splash />;
  if (!user) { window.location.replace("/login"); return <Splash />; }
  if (ctx?.is_client) { window.location.assign("/portal"); return <Splash />; }
  return <AppShell />;
}

function PortalGate() {
  const { user, ctx } = useAtlas();
  if (user === undefined || (user && !ctx)) return <Splash text="Loading your portal…" />;
  if (!user) return <Navigate to="/login" replace />;
  if (!ctx?.is_client && !ctx?.workspace) { window.location.assign("/"); return <Splash />; }
  return <PortalShell />;
}

function LoginGate() {
  const { user } = useAtlas();
  if (user === undefined) return <Splash text="Loading…" />;
  if (user) { window.location.replace("/"); return <Splash />; }
  return <Login />;
}

// Authenticated app routes, mounted under the /t/{slug} basename.
function AppRoutes() {
  return (
    <Routes>
      <Route element={<Protected />}>
        <Route path="/" element={<Navigate to="/home" replace />} />
        <Route path="/clients" element={<Clients />} />
        <Route path="/clients/:wid" element={<ClientBoard />} />
        <Route path="/files" element={<FilesApp />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/work" element={<MyWork />} />
        <Route path="/tactics/my-work" element={<MyWork />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/work/all" element={<AllWork />} />
        <Route path="/work/boards" element={<Boards />} />
        <Route path="/work/boards/:id" element={<Board />} />
        <Route path="/work/items/:id" element={<ItemRedirect />} />
        <Route path="/tactics/tasks" element={<Navigate to="/work" replace />} />
        <Route path="/dashboard" element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<Dashboard />} />
        <Route path="/companies" element={<Companies />} />
        <Route path="/companies/report" element={<CompaniesReport />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/atlas" element={<AtlasAIPage />} />
        <Route path="/ai" element={<Navigate to="/atlas" replace />} />
        <Route path="/decisions" element={<Decisions />} />
        <Route path="/missions" element={<Missions />} />
        <Route path="/portfolio" element={<Portfolio />} />
        <Route path="/knowledge" element={<Knowledge />} />
        <Route path="/admin/ingest" element={<Ingest />} />
        <Route path="/crm/organizations" element={<Organizations />} />
        <Route path="/crm/organizations/:id" element={<OrganizationDetail />} />
        <Route path="/crm/people" element={<People />} />
        <Route path="/crm/people/:id" element={<ContactProfile />} />
        <Route path="/people-team" element={<PeopleTeam />} />
        <Route path="/people-team/org-chart" element={<OrgChart />} />
        <Route path="/crm/opportunities" element={<Opportunities />} />
        <Route path="/tactics/initiatives" element={<Initiatives />} />
        <Route path="/tactics/projects" element={<Navigate to="/work/boards" replace />} />
        <Route path="/tactics/projects/:id" element={<Navigate to="/work/boards" replace />} />
        <Route path="/inventory/items" element={<Items />} />
        <Route path="/inventory/items/:id" element={<ItemDetail />} />
        <Route path="/inventory/locations" element={<Locations />} />
        <Route path="/inventory/movements" element={<Movements />} />
        <Route path="/marketing" element={<MarketingHome />} />
        <Route path="/marketing/budget" element={<Budget />} />
        <Route path="/marketing/channels" element={<Channels />} />
        <Route path="/employee-zero" element={<EmployeeZero />} />
        <Route path="/employee-zero/my-agents" element={<EmployeeZero mode="mine" />} />
        <Route path="/employee-zero/agents/:id" element={<AgentDetail />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/installed" element={<Marketplace mode="installed" />} />
        <Route path="/marketplace/categories" element={<MarketplaceCategories />} />
        <Route path="/employee-zero/activity" element={<AgentActivityPage />} />
        <Route path="/employee-zero/requests" element={<HiringRequestsPage />} />
        <Route path="/marketing/campaigns" element={<Campaigns />} />
        <Route path="/marketing/campaigns/:id" element={<CampaignDetail />} />
        <Route path="/admin/company" element={<Company />} />
        <Route path="/admin/industries" element={<Industries />} />
        <Route path="/admin/users" element={<Users />} />
        <Route path="/admin/people" element={<AdminPeople />} />
        <Route path="/admin/teams" element={<Teams />} />
        <Route path="/admin/roles" element={<Roles />} />
        <Route path="/admin/applications" element={<Applications />} />
        <Route path="/admin/integrations" element={<Integrations />} />
        <Route path="/admin/audit" element={<Audit />} />
        <Route path="/admin/knowledge" element={<Knowledge />} />
        <Route path="/admin/ai-activity" element={<AIActivity />} />
        <Route path="/admin/operations" element={<Operations />} />
        <Route path="/admin/support" element={<Support />} />
        <Route path="/admin/automation" element={<Automation />} />
        <Route path="/team-sheet" element={<TeamSheetDashboard />} />
        <Route path="/team-sheet/board" element={<CoachesBoard />} />
        <Route path="/team-sheet/drafts" element={<Drafts />} />
        <Route path="/team-sheet/compare" element={<ScenarioCompare />} />
        <Route path="/pulse" element={<PulseOverview />} />
        <Route path="/pulse/explorer" element={<PulseExplorer />} />
        <Route path="/pulse/sources" element={<PulseSources />} />
        <Route path="/team-sheet/drafts/:id" element={<DraftReview />} />
        <Route path="/team-sheet/recruiting" element={<Recruiting />} />
        <Route path="/team-sheet/upload" element={<UploadCentre />} />
        <Route path="/team-sheet/work/:id" element={<ClientProfile />} />
      </Route>
      <Route path="*" element={<Navigate to="/home" replace />} />
    </Routes>);
}

// Public, pre-tenant routes (no /t/{slug} segment).
function PublicRouter() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginGate />} />
        <Route path="/c/:slug" element={<BrandedLogin />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/portal" element={<PortalGate />}>
          <Route index element={<PortalHome />} />
          <Route path="tasks" element={<PortalTasks />} />
          <Route path="tickets" element={<PortalTickets />} />
          <Route path="tickets/:id" element={<PortalTicket />} />
          <Route path="documents" element={<PortalDocuments />} />
          <Route path="ask" element={<PortalAsk />} />
        </Route>
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>);
}

function pickDefaultSlug(tenants, last) {
  if (last && tenants.find((t) => t.slug === last)) return last;
  const main = tenants.find((t) => t.owner) || tenants.find((t) => !t.company && !t.support) || tenants.find((t) => !t.support) || tenants[0];
  return main?.slug;
}

// Sets the API tenant header synchronously (before children fetch) and loads context.
function AppRouter({ slug, tenantId }) {
  const { applyTenant } = useAtlas();
  if (getTenant() !== tenantId) setTenant(tenantId);
  useEffect(() => {
    applyTenant(tenantId);
    try { localStorage.setItem("to_last_tenant", slug); } catch (e) { /* ignore */ }
  }, [tenantId, slug, applyTenant]);
  return (
    <BrowserRouter basename={`/t/${slug}`}>
      <AppRoutes />
    </BrowserRouter>);
}

// Derives the active tenant from the URL /t/{slug} segment.
function TenantRouter() {
  const { user, tenants } = useAtlas();
  if (user === undefined) return <Splash />;
  if (!user) { window.location.replace("/login"); return <Splash />; }
  if (!tenants.length) return <Splash text="Loading your workspaces…" />;
  if (user.must_change_password) return <ForcePasswordChange />;
  const p = window.location.pathname;
  const m = p.match(/^\/t\/([^/]+)(\/.*)?$/);
  if (!m) {
    const slug = pickDefaultSlug(tenants, localStorage.getItem("to_last_tenant"));
    if (!slug) return <AccessDenied noWorkspace />;
    window.location.replace(`/t/${slug}${p === "/" ? "/home" : p}`);
    return <Splash />;
  }
  const slug = m[1];
  const t = tenants.find((x) => x.slug === slug);
  if (!t) return <AccessDenied slug={slug} />;
  return <AppRouter slug={slug} tenantId={t.id} />;
}

function Root() {
  const p = window.location.pathname;
  const isPublic = p === "/login" || p.startsWith("/c/") || p.startsWith("/invite/") ||
    p.startsWith("/forgot-password") || p.startsWith("/reset-password") ||
    p === "/portal" || p.startsWith("/portal/");
  return isPublic ? <PublicRouter /> : <TenantRouter />;
}

function App() {
  return (
    <AtlasProvider>
      <Toaster position="top-right" richColors />
      <Root />
    </AtlasProvider>);
}

export default App;
